import React from 'react';
import {
  Layout, Menu, Modal, Button, Icon,
} from 'antd';
import SplitPane from 'react-split-pane';
import styled, { css } from 'react-emotion';
import {
  width, space, color, position,
} from 'styled-system';
import { TiArrowRight } from 'react-icons/ti';
import defaultTheme from '../../styles';
import LoadSpinnerOverlay from '../LoadSpinner';

export const SideBar = styled(Layout.Sider)` &&& {
  ${space}
  ${color}
  ${width}
  /** 48px is the height of WithSubNavigation component. */
  height: calc(100vh - ${defaultTheme.heights.navBarHeight} - 48px);
}`;

export const SideMenuWrapper = styled(Menu)` &&& {
  ${space}
  ${color}
  ${width}
  text-transform: capitalize;
  overflow-y: scroll;
  overflow-x: hidden;
}`;

export const BadRequestCard = styled('div')`
  svg {
    font-size: 6em;
    color: ${defaultTheme.colors.greyscale[3]};
  }

  h1 {
    color: ${defaultTheme.colors.greyscale[3]};
    font-weight: 100;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
`;

export const SubMenu = styled(Menu.SubMenu)`
  ${color}
`;

export const ModalWrapper = styled(Modal)`
  .ant-modal-body {
    height: ${({ hasGrid }) => hasGrid && '600px'};
  }
  .ant-modal-title * {
    vertical-align: middle;
    text-transform: capitalize;
  }
`;

export const Back = styled(Button)`
  margin-right: 24px;
  > * {
    vertical-align: middle !important;
  }
`;

export const Right = styled(TiArrowRight)`
  margin: 0px 5px 0px 5px;
`;

export const Loading = styled(LoadSpinnerOverlay)` &&& {
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: white;
}`;

export const MenuItem = styled(Menu.Item)` &&& {
  ${space}
  ${color}
  ${width}
}`;

export const Main = styled(Layout.Content)` &&& {
  ${space}
  ${color}
  ${width}
  ${position}
  display: block;
}`;

export const GridWrapper = styled('span')`
  /*
    This is the only way to ensure the delete button is hidden and not-clickable.
    Of course ideally we can have a proper disable delete button functionality in
    Bkt-grid.
  */
  .ReactVirtualized__Table__rowColumn .inactive > div:nth-child(2) {
    display: none !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
`;

export const Empty = styled('div')`
  display: flex;
  justify-content: center;
  font-size: 1.5rem;
  padding-top: 2rem;
`;

export const BulkEditButtons = styled(Button.Group,
  { shouldForwardProp: prop => !['compact'].includes(prop) })`
  margin-right: 8px;
`;

export const PageBarButton = styled(Button)`
  margin-left: 8px;
`;

export const DataButton = styled(PageBarButton)`
  ${({ status }) => {
    if (status === 'done') return 'color: green';
    if (status === 'error') return 'color: red';
    return '';
  }}
  padding: 0 10px;
  span {
    margin-left: 4px !important;
  }
`;

// Antd requires this to be added so that the tooltip will hide correctly
// after mouse leave on any styled-component wrapped buttons.
/* eslint no-underscore-dangle: 0 */
DataButton.__ANT_BUTTON = true;

export const FooterButton = PageBarButton;

export const PagainationWrapper = styled(Button.Group)`
  position: absolute;
  bottom: 0;
  margin: 15px;
`;

export const Limit = styled('span')`
  font-weight: bold;
  margin-right: 0.4rem;
`;

export const DataLink = styled('div')`
  position: absolute;
  bottom: 0;
  right: 0;
  margin: 1rem;
`;

export const StyledIcon = styled(props => <Icon theme="filled" {...props} />)` &&& {
  ${color}
  ${space}
}`;

export const DataLinkText = styled('span')`
  display: inline-block;
  margin-left: 0.1rem;
  padding: 4px 2px;
  font-size: 95%;
`;

export const Circle = styled('div')`
  background: ${({ theme }) => theme.colors.greyscale[2]};
  height: 1em;
  width: 1em;
  border-radius: 1em;
`;

export const LineCircle = styled('div')`
  height: 1em;
  width: 1em;
  border: 2px solid ${({ theme }) => theme.colors.greyscale[2]};
  border-radius: 1em;
`;

export const SemiCircle = styled('div')`
  background: ${({ theme }) => theme.colors.greyscale[2]};
  height: 1em;
  width: 0.5em;
  margin-right: 0.5em;
  border-bottom-left-radius: 1em;
  border-top-left-radius: 1em;
`;

export const ShiftTask = styled('div')`
  div {
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 12px !important;
    line-height: 1.2;
  }
  margin-bottom: 5px;
`;

export const SwitchLabel = styled('span')`
  margin-right: 1rem;
`;

export const hideFileList = css`.ant-upload-list { display: none; }`;

export const ModalDeleteWrapper = styled('div')`
  position: absolute;
  right: 24px;
  margin-top: -12px;
  z-index: 999;
`;

export const ErrorMsgWrapper = styled('div')`
  .ant-list {
    height: 60vh;
    overflow-y: scroll;
  }

  .ant-list-item {
    padding: 4px 12px;
    font-size: 90%;
  }
`;

export const MenuSearchWrapper = styled('div')`
  padding: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.greyscale[3]};
  border-right: 1px solid ${({ theme }) => theme.colors.greyscale[3]};
`;

export const BottomMenuWrapper = styled(Menu)` &&& {
  ${space}
  ${color}
  ${width}
  height: ${({ getTopPaneSize, theme }) => {
    // This calculates the bottom panel height that fits within the sidebar so that
    // it is not always 100vh. In order to do that, this requires the actual height
    // of the top panel.
    // 105 is the hard number of search bar (defined here) + subnav height.
    const topPaneSize = (typeof getTopPaneSize === 'number')
      ? `${getTopPaneSize + 105}px` : getTopPaneSize;
    return `calc(100vh - ${topPaneSize} - ${theme.heights.navBarHeight})`;
  }};

  text-transform: capitalize;
  overflow-y: scroll;
  overflow-x: hidden;
  border-top: 1px solid ${({ theme }) => theme.colors.greyscale[3]};
  padding-top: 12px;

  .ant-tree li {
    padding: 0;
    & > * {
      font-size: 13px;
      height: 22px;
    }

    .ant-tree-switcher-icon {
      display: none;
    }

    .ant-tree-checkbox,
    .ant-tree-title {
      padding: 0;
      margin: 0;
    }

    .ant-tree-checkbox {
      zoom: .9;
    }

    .ant-tree-node-content-wrapper {
      line-height: 18px;
    }
  }
}`;

export const SplitPanel = styled(SplitPane)` &&& {
  position: relative !important;
  height: 100vh !important;
  border-right: 1px solid ${({ theme }) => theme.colors.greyscale[3]};

  .Resizer {
    opacity: .6;
    z-index: 1;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    -moz-background-clip: padding;
    -webkit-background-clip: padding;
    background-clip: padding-box;
  }

  .Resizer:hover {
    -webkit-transition: all .5s ease;
    transition: all .5s ease;
  }

  .Resizer.horizontal {
    height: 7px;
    margin: -3px 0;
    border-top: 3px solid rgba(255, 255, 255, 0);
    border-bottom: 3px solid rgba(255, 255, 255, 0);
    cursor: row-resize;
    width: 100%;
  }

  .Resizer.horizontal:hover {
      border-top: 3px solid rgba(0, 0, 0, 0.5);
      border-bottom: 3px solid rgba(0, 0, 0, 0.5);
  }
}`;
